import React, { useCallback, useEffect, useState } from 'react';
import {
    DataGridPremium,
    GridRenderEditCellParams,
    GridColDef,
    useGridApiContext,
    GridRowModel,
    GridCellParams,
    GridRenderCellParams,
    GridRowSelectionModel,
    GridEventListener,
} from '@mui/x-data-grid-premium';
import { convertToCustomElement } from '../formbird/CustomElementWrapper';
import { useFormbird } from '../formbird/FormbirdContext';
import { Avatar, MenuItem, Select, SelectChangeEvent, Tooltip, Typography, Box } from '@mui/material';
import { changedDocumentService, routerService } from '../services';
import { ComponentDefinition } from '@formbird/types';

interface Feature {
    type: string;
    properties: {
        type: string;
        linkAssets: LinkAsset[]
    };
}

type MarkerType = `cww-${'blue' | 'green' | 'pink' | 'pinkWhite' | 'pinkYellow' | 'purpleInPink' |
    'purpleInPinkWhite' | 'purpleInPinkYellow' | 'purpleInRed' | 'purpleInYellow' | 'purple' |
    'purpleYellow' | 'red' | 'white' | 'whiteYellow' | 'yellow'}ValveMarker-30x30.png`;


interface LinkAsset {
    assetNumber: string;
    name: string;
    asset_group: string;
    documentId: string;
    alignmentDirection?: number;
    valveStatus: string;
    includeInCase?: boolean;
    markerType?: MarkerType;
}

interface LocationGeo {
    features: Feature[];
}

interface CwwValveMarkerSelectorDefinition {
    selectedAssetData?: string;
    selectedAssetType?: string;
}

const MARKER_TYPES = [
    'blue',
    'green',
    'pink',
    'pinkWhite',
    'pinkYellow',
    'purpleInPink',
    'purpleInPinkWhite',
    'purpleInPinkYellow',
    'purpleInRed',
    'purpleInYellow',
    'purple',
    'purpleYellow',
    'red',
    'white',
    'whiteYellow',
    'yellow'
];

const MarkerTypeEditor = (props: GridRenderEditCellParams) => {
    const { id, value, field } = props;
    const apiRef = useGridApiContext();
    const handleChange = (event: SelectChangeEvent) => {
        apiRef.current.setEditCellValue({ id, field, value: event.target.value });
        apiRef.current.stopCellEditMode({ id, field });
    };
    return (<Select value={value} onChange={handleChange} label='Marker Type'>
        {MARKER_TYPES.map((markerType) => (
            <MenuItem value={markerType} key={markerType}>
                <img src={`/api/getFile/cww-${markerType}ValveMarker-30x30.png`} alt={markerType} />
            </MenuItem>
        ))}
    </Select>);
};

const RenderMarkerIcon = (props: GridRenderCellParams) => {
    return (
        <Tooltip title='Double click to edit' arrow>
            <Avatar variant='square' src={`/api/getFile/cww-${props.value}ValveMarker-30x30.png`} alt={props.value} />
        </Tooltip>
    );
};

const RenderAlignmentDirection = (props: GridRenderCellParams) => {
    return (
        <Tooltip title='Double click to edit' arrow>
            <Typography variant='body1'>{props.value}</Typography>
        </Tooltip>
    );
};


const columns: GridColDef[] = [
    { field: 'name', type: 'string', headerName: 'Valve', flex: 2 },
    { field: 'asset_group', type: 'string', headerName: 'Asset Group', flex: 0.5 },
    { field: 'valveStatus', type: 'string', headerName: 'Valve Status', flex: 0.5 },
    {
        field: 'markerType',
        type: 'singleSelect',
        headerName: 'Marker Type',
        valueOptions: MARKER_TYPES,
        flex: 0.5,
        editable: true,
        renderEditCell: MarkerTypeEditor,
        renderCell: RenderMarkerIcon
    },
    {
        field: 'alignmentDirection',
        type: 'number',
        headerName: 'Alignment (degrees N)',
        editable: true,
        flex: 0.5,
        renderCell: RenderAlignmentDirection
    },
];

function markerColor(markerType?: string): string {
    const regExp = /cww-(\w+)ValveMarker-30x30.png/;
    return markerType?.match(regExp)?.[1] ?? '';
}

export default function ValveMarkerSelector() {
    const fb = useFormbird<CwwValveMarkerSelectorDefinition>();

    const fieldName = fb.componentDefinition.selectedAssetData ?? 'locationGeo';
    const selectedAssetType = fb.componentDefinition.selectedAssetType;
    const [fieldValue, setFieldValue] = useState(fb.document[fieldName] as LocationGeo);
    const [visibility, setVisibility] = useState(true);

    useEffect(() => {
        const docSub = changedDocumentService.watch(fieldName, fb.document.documentId, fb.registeredControlName)
            .subscribe((newValue: any) => {
                setFieldValue(newValue);
            });

        return () => docSub.unsubscribe();
    }, []);



    useEffect(() => {
        // formbirdContext.componentDefinition should be taking care of this, but I have no idea why
        // no re-render occurs when `setComponentDefinition(newValue)` is called
        // the visible state change is inlined here as a workaround
        const docSub = changedDocumentService.watchComponentDef(fb.fieldName, fb.template.documentId)
            .subscribe((newValue: ComponentDefinition) => {
                setVisibility(newValue.visible ?? true)
            });

        return () => docSub.unsubscribe();
    }, []);

    const valueChanged = (newFieldValue: any) => {
        changedDocumentService.valueChanged({
            ...fb,
            fieldName: fieldName
        }, newFieldValue, fb.registeredControlName);
    };

    const getData = (locationGeo: LocationGeo) =>
        locationGeo.features.filter(it => it.properties.type === selectedAssetType)
            .map(it => it.properties.linkAssets)
            .flat()
            .filter(asset => asset.asset_group === 'WN-VALVE' || asset.asset_group === 'AN-VALVE')
            .map(it => ({
                ...it,
                markerType: markerColor(it.markerType)
            }));

    const data = fieldValue ? getData(fieldValue) : [];

    const selectionModel = data.filter(it => it.includeInCase === true).map(it => it.documentId);

    const processRowUpdate = useCallback((newRow: GridRowModel) => {
        const newFieldValue = structuredClone(fieldValue);
        for (const feature of newFieldValue.features) {
            // only iterate through assets that we know have the properties required
            if (feature.properties.type !== fb.componentDefinition.selectedAssetType && feature.properties.linkAssets === undefined) {
                continue;
            }
            // also filter out assets which are valves
            const asset = feature.properties.linkAssets.find(it => it.assetNumber === newRow.assetNumber && it.asset_group === newRow.asset_group);
            if (asset) {
                asset.markerType = `cww-${newRow.markerType}ValveMarker-30x30.png`;
                if (newRow.markerType === 'red' || newRow.markerType === 'purpleInRed') {
                    asset.valveStatus = 'Closed';
                }
                asset.alignmentDirection = newRow.alignmentDirection;
                valueChanged(newFieldValue);
                break;
            }
        }
        return newRow;
    }, [fieldValue]);

    const onCellClick: GridEventListener<'cellClick'> = (param: GridCellParams) => {
        if (param.field === 'name') {
            routerService.navigateByUrl(`/form/${param.row.documentId}`);
        }
    };

    const onSelectionChange = (selectionModel: GridRowSelectionModel) => {
        const newFieldValue = structuredClone(fieldValue);
        for (const feature of newFieldValue.features) {
            // only iterate through assets that we know have the properties required
            if (feature.properties.type !== fb.componentDefinition.selectedAssetType && feature.properties.linkAssets === undefined) {
                continue;
            }
            for (const asset of feature.properties.linkAssets) {
                asset.includeInCase = selectionModel.includes(asset.documentId)
            }
        }
        valueChanged(newFieldValue);
    };


    return visibility ? (
        <>
            <Typography variant='body1'>{fb.componentDefinition.label}</Typography>
            <Box sx={{ height: '100%', display: 'flex', width: '100%', minWidth: 300 }}>
                <Box sx={{ flexGrow: 1 }}>
                    <DataGridPremium
                        autoHeight
                        columns={columns}
                        rows={data}
                        getRowId={r => r.documentId}
                        onCellClick={onCellClick}
                        processRowUpdate={processRowUpdate}
                        checkboxSelection
                        onRowSelectionModelChange={onSelectionChange}
                        rowSelectionModel={selectionModel}
                    />
                </Box>
            </Box>
        </>
    ) : null;
}

convertToCustomElement('cww-valve-marker-selector', ValveMarkerSelector);
